import { type Colors } from '@factorialco/deprecated-design-system'
import {
  AnalyticsPageEmployeeNamesFragment,
  AnalyticsPageLegalEntitiesFragment,
  AnalyticsPageLocationsFragment,
  TeamsTeam,
} from 'generated/resources/graphql'

import { type GroupByType } from 'modules/performance_analytics/components/helpers'

import { CHART_CONFIG } from './constants'
import { createFilterValueTransformer } from './filterValueTransformers'
import type { ChartDataItem } from './types'

const GROUP_BY_TO_FILTER_PATH_MAPPING: Record<Exclude<GroupByType, null>, string> = {
  gender: 'gender',
  legal_entity: 'legalEntityId',
  manager: 'managerId',
  nationality: 'nationality',
  team: 'teamId',
  workspace: 'workspace',
}

export const getFilterPathForGroupBy = (groupBy: GroupByType): string => {
  if (groupBy === null) {
    throw new Error('Cannot get filter path for null groupBy')
  }
  return GROUP_BY_TO_FILTER_PATH_MAPPING[groupBy]
}

const hasInProperty = (obj: unknown): obj is { in?: unknown[] } => {
  return Boolean(obj && typeof obj === 'object' && 'in' in obj)
}

export const getColorsBasedOnFilters = (
  data: ChartDataItem[],
  dataTableFilters: Record<string, unknown>,
  groupBy?: GroupByType,
  legalEntities?: AnalyticsPageLegalEntitiesFragment[],
  teams?: Pick<TeamsTeam, 'id' | 'name'>[],
  locations?: AnalyticsPageLocationsFragment[],
  employeesNames?: AnalyticsPageEmployeeNamesFragment[]
): Colors[] => {
  if (!groupBy) {
    return data.map(() => 'viridian1000')
  }

  const resolvedGroupBy = getFilterPathForGroupBy(groupBy)
  const currentFilter = dataTableFilters[resolvedGroupBy]
  const selectedValues = hasInProperty(currentFilter) ? currentFilter.in || [] : []

  if (selectedValues.length === 0) {
    return data.map(() => 'viridian1000')
  }

  // Create transformer for consistent value handling
  const transformer = createFilterValueTransformer({
    groupBy,
    legalEntities,
    teams,
    locations,
    employeesNames,
  })

  return data.map((item) => {
    const displayName = item[CHART_CONFIG.indexBy]?.toString()
    const rawValue = transformer.transformToRawValue(displayName)
    return selectedValues.includes(rawValue) ? 'viridian1000' : 'grey400'
  })
}
